import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Home } from './routes/Home';
import { Checkin } from './routes/Checkin';
import { LeaveSubmit } from './routes/LeaveSubmit';
import { OTSubmit } from './routes/OTSubmit';
import { Quota } from './routes/Quota';
import { Slip } from './routes/Slip';
import { Pair } from './routes/Pair';
import { Claim } from './routes/Claim';
import { Landing } from './routes/Landing';
import { Signup } from './routes/Signup';
import { Profile } from './routes/Profile';
import { RespondInfo } from './routes/RespondInfo';
import { OwnerDashboard } from './routes/owner/Dashboard';
import { OwnerOnboard } from './routes/owner/Onboard';
import { OwnerDriveConfig } from './routes/owner/DriveConfig';
import { PayrollCycle } from './routes/owner/PayrollCycle';
import { PayrollRehearsal } from './routes/owner/PayrollRehearsal';
import { PayrollEmailSlips } from './routes/owner/PayrollEmailSlips';
// OwnerReconcile / CloseReadiness / OwnerPayroll merged into PayrollCycle hub —
// the three /owner/* routes below redirect into the hub with the matching tab.
import { PayrollAnalysis } from './routes/owner/PayrollAnalysis';
import { OwnerAudit } from './routes/owner/Audit';
import { OwnerPendingChanges } from './routes/owner/PendingChanges';
import { OwnerDeductions } from './routes/owner/Deductions';
import { OwnerSettings } from './routes/owner/Settings';
import { AISettings } from './routes/owner/AISettings';
import { OwnerYearEnd } from './routes/owner/YearEnd';
import { OwnerTermination } from './routes/owner/Termination';
import { HRDashboard } from './routes/hr/Dashboard';
// /hr/pending merged into the /approvals hub — old route now redirects.
import { HREmployees } from './routes/hr/Employees';
import { People } from './routes/hr/People';
import { Time } from './routes/hr/Time';
import { Wage } from './routes/owner/Wage';
import { OwnerTemplates } from './routes/owner/Templates';
import { WorkflowGuide } from './routes/owner/WorkflowGuide';
import { OwnerBlueprint } from './routes/owner/Blueprint';
import { OwnerOnlyPage } from './routes/owner/OwnerOnly';
import { OwnerSetup } from './routes/owner/Setup';
import { OwnerHrPermissions } from './routes/owner/HrPermissions';
import { OwnerApprovalChain } from './routes/owner/ApprovalChain';
import { OwnerOrgStructure } from './routes/owner/OrgStructure';
import { OwnerCompensation } from './routes/owner/Compensation';
import { NotificationSettings } from './routes/owner/NotificationSettings';
import { OwnerRegisterEmployees } from './routes/owner/RegisterEmployees';
import { RosterImport } from './routes/hr/RosterImport';
import { EmployeeAttendance } from './routes/hr/EmployeeAttendance';
import { HREscalations } from './routes/hr/Escalations';
import { Swap } from './routes/Swap';
import { ShiftChange } from './routes/ShiftChange';
import { ShiftTime } from './routes/ShiftTime';
import { AttendanceCorrection } from './routes/AttendanceCorrection';
import { HRAllowances } from './routes/hr/Allowances';
import { HRHolidays } from './routes/hr/Holidays';
import { HRQuotas } from './routes/hr/Quotas';
import { HRLeavePackages } from './routes/hr/LeavePackages';
import { HRFollowup } from './routes/hr/Followup';
import { HRAllowanceMaster } from './routes/hr/AllowanceMaster';
import { HRCommission } from './routes/hr/Commission';
import { HRShifts } from './routes/hr/Shifts';
import { Approvals } from './routes/Approvals';
import { ApprovalSummary } from './routes/owner/ApprovalSummary';
import { Tickets } from './routes/Tickets';
// /hr/tickets and /hr/emergency-leave merged into the /approvals hub.
import { ConfirmClockout } from './routes/ConfirmClockout';
import { AskAI } from './routes/AskAI';
import { Find } from './routes/Find';
import { MyRequests } from './routes/MyRequests';
import { MyTeam } from './routes/MyTeam';
import { HistoricalImport } from './routes/owner/HistoricalImport';
import { KcashFromSheet } from './routes/owner/KcashFromSheet';
import { SheetAdjustmentsImport } from './routes/owner/SheetAdjustmentsImport';
import { MyAttendance } from './routes/MyAttendance';
import { HRAttendanceToday } from './routes/hr/AttendanceToday';
import { Factsheet } from './routes/Factsheet';
import { ContractSign } from './routes/ContractSign';
import { HRContracts } from './routes/hr/Contracts';
import { HRLetters } from './routes/hr/Letters';
import { HRLetterForm } from './routes/hr/LetterForm';
import { MyLetters } from './routes/MyLetters';
import { HRContractNew } from './routes/hr/ContractNew';
import { MyPolicies } from './routes/MyPolicies';
import { PolicyRead } from './routes/PolicyRead';
import { LetterSign } from './routes/LetterSign';
import { HRPolicies } from './routes/hr/Policies';
import { HRPolicyNew } from './routes/hr/PolicyNew';
import { HRPolicyAcks } from './routes/hr/PolicyAcks';
import { DocKitHome } from './routes/hr/doc-kit/DocKitHome';
import { DocKitSelect } from './routes/hr/doc-kit/DocKitSelect';
import { DocKitWizard } from './routes/hr/doc-kit/DocKitWizard';
import { DocKitBatch } from './routes/hr/doc-kit/DocKitBatch';
import { DocumentList } from './routes/hr/doc-kit/DocumentList';
import { DocumentDetail } from './routes/hr/doc-kit/DocumentDetail';
import { Loading } from './components/layout/Loading';
import { ErrorScreen } from './components/layout/ErrorScreen';
import { PendingApprovalScreen } from './routes/PendingApproval';
import { WebLogin } from './routes/WebLogin';
import { Launcher } from './routes/Launcher';

export function App() {
  const { profile, status, error, refreshAuth } = useAuth();

  if (status === 'loading') return <Loading />;
  // Web-login flow: when no LIFF context is available, route everything to
  // the /login page (no LINE required). User logs in with email+password,
  // setAuth flips status to 'ok', and we re-render into the normal app.
  if (status === 'no-liff') {
    return (
      <Routes>
        <Route path="/login" element={<WebLogin />} />
        {/* สมัครทดลองใช้ด้วยอีเมล+รหัสผ่าน (ไม่ผ่าน LINE) — ใช้ได้จากเบราว์เซอร์ */}
        <Route path="/signup" element={<Signup />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  if (status === 'error') return <ErrorScreen message={error ?? 'Unknown error'} onRetry={() => void refreshAuth(true)} />;
  // 'unpaired' = LINE OK but no user row yet → Landing lets them pick
  // signup-as-owner vs pair-as-employee.
  // 'unpaired' (ยังไม่มี user row) หรือ มี jwt ค้างแต่ profile ว่าง → หน้า onboarding.
  // ต้องครอบด้วย <Routes> ทั้งคู่ ไม่งั้นปุ่มใน Landing (Link → /claim /pair /signup)
  // เปลี่ยน URL แต่หน้าไม่เปลี่ยน (เด้งกลับ Landing) = "กดแล้วไม่ไป".
  if (status === 'unpaired' || !profile) {
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="/pair" element={<Pair />} />
        <Route path="/claim" element={<Claim />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }
  // Tenant created but platform admin hasn't approved yet — holding screen.
  if (profile.tenant_status === 'pending_approval') {
    return <PendingApprovalScreen profile={profile} onRefresh={() => void refreshAuth(true)} />;
  }

  // Role-based landing: OWNER → /owner dashboard; HR → /hr; others → Home.
  // Users can still hit /home or /employee links from the role dashboards
  // to access the employee menu (check-in, leave, OT, slip).
  const rootElement =
    profile.role === 'OWNER' ? <Navigate to="/owner" replace />
    : profile.role === 'HR' || profile.role === 'MANAGER' ? <Navigate to="/hr" replace />
    : <Home />;

  return (
    <Routes>
      <Route path="/" element={rootElement} />
      <Route path="/launcher" element={<Launcher />} />
      <Route path="/home" element={<Home />} />
      <Route path="/checkin" element={<Checkin />} />
      <Route path="/leave" element={<LeaveSubmit />} />
      <Route path="/ot" element={<OTSubmit />} />
      <Route path="/swap" element={<Swap />} />
      <Route path="/shift-change" element={<ShiftChange />} />
      <Route path="/shift-time" element={<ShiftTime />} />
      <Route path="/attendance-correction" element={<AttendanceCorrection />} />
      <Route path="/quota" element={<Quota />} />
      <Route path="/slip" element={<Slip />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/respond-info/:leaveId" element={<RespondInfo />} />
      <Route path="/approvals" element={<Approvals />} />
      <Route path="/tickets" element={<Tickets />} />
      <Route path="/confirm-clockout" element={<ConfirmClockout />} />
      <Route path="/ask" element={<AskAI />} />
      <Route path="/find" element={<Find />} />
      <Route path="/me/requests" element={<MyRequests />} />
      <Route path="/me/team" element={<MyTeam />} />
      <Route path="/me/attendance" element={<MyAttendance />} />
      <Route path="/factsheet" element={<Factsheet />} />
      {/* สัญญาจ้าง — พนักงานเปิดจากการ์ดไลน์มาที่ /contract/:id เพื่ออ่านและลงนาม */}
      <Route path="/contract/:id" element={<ContractSign />} />
      <Route path="/policies" element={<MyPolicies />} />
      <Route path="/me/letters" element={<MyLetters />} />
      {/* ลงนามรับทราบหนังสือเตือนบนจอ (fb#176) — ลิงก์จากการ์ดไลน์มาที่นี่ */}
      <Route path="/me/letters/:id/sign" element={<LetterSign />} />
      <Route path="/policies/:id" element={<PolicyRead />} />
      {/* ผูกบริษัทเพิ่ม (คนเดียวอยู่ได้หลายบริษัท) — เข้าถึงได้ทั้งที่ล็อกอินอยู่
          ผ่านปุ่ม "เพิ่มบริษัท" ใน TenantSwitcher. /auth/pair คืน JWT ของบริษัท
          ใหม่ → setAuth → เด้งเข้าบริษัทที่เพิ่งเพิ่ม. */}
      <Route path="/pair" element={<Pair />} />

      {/* HR (MANAGER has the same operational access as HR) */}
      {(profile.role === 'HR' || profile.role === 'OWNER' || profile.role === 'MANAGER') && (
        <>
          <Route path="/hr" element={<HRDashboard />} />
          <Route path="/owner/guide" element={<WorkflowGuide />} />
          <Route path="/owner/blueprint" element={<OwnerBlueprint />} />
          <Route path="/owner/setup" element={<OwnerSetup />} />
          <Route path="/owner/hr-permissions" element={<OwnerHrPermissions />} />
          <Route path="/owner/approval-chain" element={<OwnerApprovalChain />} />
          <Route path="/owner/approval-summary" element={<ApprovalSummary />} />
          <Route path="/owner/org" element={<OwnerOrgStructure />} />
          <Route path="/owner/comp" element={<OwnerCompensation />} />
          <Route path="/owner/notifications" element={<NotificationSettings />} />
          <Route path="/owner/register-employees" element={<OwnerRegisterEmployees />} />
          <Route path="/owner/import-historical" element={<HistoricalImport />} />
          <Route path="/hr/roster-import" element={<RosterImport />} />
          <Route path="/hr/attendance-today" element={<Navigate to="/hr/time#today" replace />} />
          {/* Legacy approval pages → redirect to consolidated /approvals hub
              (preserved so old DailyBriefing/LIFF rich-menu deep links still resolve). */}
          <Route path="/hr/pending" element={<Navigate to="/approvals#changes" replace />} />
          <Route path="/hr/tickets" element={<Navigate to="/approvals#tickets" replace />} />
          <Route path="/hr/emergency-leave" element={<Navigate to="/approvals#emergency" replace />} />
          {/* People hub: employees + onboard + termination + followup + pending-changes */}
          <Route path="/hr/people" element={<People />} />
          {/* 🚨 สัญญาจ้างมีเลขบัตร ปชช. + เลขบัญชี + ค่าจ้างรายคน — ปิด MANAGER
              (บล็อกนี้เปิดให้ MANAGER ด้วย จึงต้องกันซ้ำอีกชั้นตรงนี้ ให้ตรงกับ
              requireHqOnly() ฝั่ง Worker · ซ่อนเมนูอย่างเดียวไม่พอ เพราะพิมพ์
              URL ตรงเข้ามาได้) */}
          {(profile.role === 'OWNER' || profile.role === 'HR') && (
            <>
              <Route path="/hr/contracts" element={<HRContracts />} />
              <Route path="/hr/letters" element={<HRLetters />} />
              <Route path="/hr/letters/new" element={<HRLetterForm key="create" mode="create" />} />
              <Route path="/hr/letters/:id/edit" element={<HRLetterForm key="edit" mode="edit" />} />
              <Route path="/hr/contracts/new" element={<HRContractNew />} />
              <Route path="/hr/policies" element={<HRPolicies />} />
              <Route path="/hr/policies/new" element={<HRPolicyNew />} />
              <Route path="/hr/policies/:id/acks" element={<HRPolicyAcks />} />
              <Route path="/hr/doc-kit" element={<DocKitHome />} />
              <Route path="/hr/doc-kit/select" element={<DocKitSelect />} />
              <Route path="/hr/doc-kit/wizard/:step" element={<DocKitWizard />} />
              <Route path="/hr/doc-kit/batches/:id" element={<DocKitBatch />} />
              <Route path="/hr/documents" element={<DocumentList />} />
              <Route path="/hr/documents/:id" element={<DocumentDetail />} />
            </>
          )}
          {/* Time hub: settings + shifts + attendance-today + holidays + leave-packages */}
          <Route path="/hr/time" element={<Time />} />
          <Route path="/hr/attendance" element={<EmployeeAttendance />} />
          <Route path="/hr/escalations" element={<HREscalations />} />
          <Route path="/hr/employees" element={<Navigate to="/hr/people#all" replace />} />
          <Route path="/hr/followup" element={<Navigate to="/hr/people#followup" replace />} />
          <Route path="/hr/probation" element={<Navigate to="/hr/people#probation" replace />} />
          <Route path="/hr/allowances" element={<HRAllowances />} />
          <Route path="/hr/holidays" element={<Navigate to="/hr/time#holidays" replace />} />
          <Route path="/hr/quotas" element={<HRQuotas />} />
          <Route path="/hr/leave-packages" element={<Navigate to="/hr/time#leave" replace />} />
          {/* /hr/followup → redirected above to /hr/people#followup */}
          <Route path="/hr/allowance-master" element={<Navigate to="/owner/wage#allowance" replace />} />
          {/* Wage hub: analysis + commission settings + year-end + allowance + deduction */}
          <Route path="/owner/wage" element={<Wage />} />
          {/* Commission: packages + runs merged into single hub with tabs. */}
          <Route path="/hr/commission" element={<HRCommission />} />
          {/* packages ย้ายไปอยู่ใน Wage hub แล้ว; commission hub เหลือแค่ runs */}
          <Route path="/hr/commission-packages" element={<Navigate to="/owner/wage#commission" replace />} />
          <Route path="/hr/commission-runs" element={<Navigate to="/hr/commission" replace />} />
          <Route path="/hr/shifts" element={<Navigate to="/hr/time#shifts" replace />} />
          {/* Payroll prep is HR's job (reconcile + run draft + submit for approval).
              Owner-only actions (approve / send slips) gate on the server. */}
          {/* Payroll cycle: reconcile + close-readiness + run merged into single hub with tabs. */}
          <Route path="/owner/cycle" element={<PayrollCycle />} />
          {/* ซ้อมทำเงินเดือน — ดูผลคำนวณก่อนรอบจริง ไม่บันทึกอะไร (TN Riches 22 ก.ย. 2569) */}
          <Route path="/owner/rehearsal" element={<PayrollRehearsal />} />
          <Route path="/owner/payroll/email-slips" element={<PayrollEmailSlips />} />
          <Route path="/owner/kcash" element={<KcashFromSheet />} />
          <Route path="/owner/sheet-import" element={<SheetAdjustmentsImport />} />
          <Route path="/owner/reconcile" element={<Navigate to="/owner/cycle#reconcile" replace />} />
          <Route path="/owner/close-readiness" element={<Navigate to="/owner/cycle#close" replace />} />
          <Route path="/owner/payroll" element={<Navigate to="/owner/cycle#payroll" replace />} />
          <Route path="/owner/payroll-analysis" element={<Navigate to="/owner/wage#analysis" replace />} />
        </>
      )}

      {/* หน้าเจ้าของเท่านั้น — คนอื่น (HR/MANAGER) กดลิงก์มาถึงต้องเห็นว่า "ทำไม" ไม่ใช่ตกไป `*` แล้วเด้ง
          กลับแดชบอร์ดเงียบ ๆ (TN Riches 20 ก.ย. 2569: HR กด "แก้ไข" ในพิมพ์เขียวแล้วไปโผล่แดชบอร์ด) */}
      {profile.role !== 'OWNER' && (
        <>
          <Route path="/owner/settings" element={<OwnerOnlyPage />} />
          <Route path="/owner/templates" element={<OwnerOnlyPage />} />
          <Route path="/owner/year-end" element={<Navigate to="/owner/wage#year-end" replace />} />
        </>
      )}

      {/* Owner */}
      {profile.role === 'OWNER' && (
        <>
          <Route path="/owner" element={<OwnerDashboard />} />
          <Route path="/owner/onboard" element={<Navigate to="/hr/people#onboard" replace />} />
          <Route path="/owner/drive" element={<Navigate to="/owner/settings#drive" replace />} />
          <Route path="/owner/audit" element={<Navigate to="/owner/settings#audit" replace />} />
          <Route path="/owner/pending-changes" element={<Navigate to="/hr/people#changes" replace />} />
          <Route path="/owner/deductions" element={<Navigate to="/owner/wage#deduction" replace />} />
          <Route path="/owner/settings" element={<OwnerSettings />} />
          <Route path="/owner/templates" element={<OwnerTemplates />} />
          <Route path="/owner/ai" element={<Navigate to="/owner/settings#ai" replace />} />
          <Route path="/owner/year-end" element={<OwnerYearEnd />} />
          <Route path="/owner/termination" element={<Navigate to="/hr/people#termination" replace />} />
        </>
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
